import { Box, LinearProgress, Typography } from '@mui/material';
import useAppTheme from 'hooks/useAppTheme';
import useCustomerInfo from 'hooks/useCustomerInfo';
import { ThemePalette } from 'mui.theme';
import { useMemo, useState } from 'react';
import { useAppSelector } from 'store/hook';
import TreeMapDiv from 'views/FuelHistoricalAnalysis/Components/BodyDiv/TreeMapDiv';
import ChartDataToggler from 'views/FuelHistoricalAnalysis/Components/ChartDataToggler';
import { type fuelMeasureType } from 'views/FuelHistoricalAnalysis/types/fuelAnalytics.types';
import SortBy from './SortBy';
import FuelAssetAnalyticsSection from './FuelAssetAnalyticsSection';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface Props {
  fuelMeasure?: fuelMeasureType;
  assetTypeKey?: 'fleet' | 'hauler' | 'loader';
  aggregation: string;
  summary: any;
}

const DetailedAnalysis = ({ assetTypeKey, aggregation, summary }: Props) => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { unitSystem } = useCustomerInfo();

  const {
    fleetDeviceDetails,
    loadingStates: { isLoadingFuelTreemapData },
  } = useAppSelector((state) => state.fuelAnalyticsReducer);

  const assetType = useMemo(() => {
    if (assetTypeKey === 'fleet') return assetTypeKey;
    else if (assetTypeKey === 'loader') return 'loader';
    else if (assetTypeKey === 'hauler') return 'haul_truck';
    return null;
  }, [assetTypeKey]);

  const selectedAssetTypTreemapDetails = useMemo(() => {
    if (assetType === 'fleet') {
      return fleetDeviceDetails.map((item) => ({
        ...item,
        fuel_consumed_per_ton: Number(
          Number(item.fuel_consumed_per_ton)?.toFixed(3) ?? 0
        ),
      }));
    } else {
      return fleetDeviceDetails
        .filter((item) => item.asset_type === assetType)
        .map((item) => ({
          ...item,
          fuel_consumed_per_ton: Number(
            Number(item.fuel_consumed_per_ton)?.toFixed(3) ?? 0
          ),
        }));
    }
  }, [assetType, fleetDeviceDetails]);

  const [fuelMeasure, setFuelMeasure] =
    useState<fuelMeasureType>('consumption');

  const externalTotal = useMemo(() => {
    if (!summary || !assetTypeKey) return null;
    let measureKey = '';
    switch (fuelMeasure) {
      case 'consumption':
        measureKey = 'total_fuel_consumption_l';
        break;
      case 'co2_emission':
        measureKey = 'CO2_used';
        break;
      case 'cost':
        measureKey = 'fuel_cost_incurred';
        break;
      case 'avg_fuel_rate':
        measureKey = 'avg_fuel_rate';
        break;
      case 'fuel_consumed_per_ton':
        measureKey = 'fuel_consumed_per_ton';
        break;
      default:
        return null;
    }
    const finalKey = `${assetTypeKey}_${measureKey}`;
    return summary[finalKey];
  }, [summary, assetTypeKey, fuelMeasure]);

  const handleFuelMeasure = (value: fuelMeasureType) => {
    if (value) {
      setFuelMeasure(value);
    }
  };

  const [sortBy, setSortBy] = useState<string>('high to low');

  const sortOptionLabels: Record<string, string> = {
    'high to low': t('fuel_usage_sort_high_to_low'),
    'low to high': t('fuel_usage_sort_low_to_high'),
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: '600',
              paddingTop: '5px',
              color: palette.textSecondary,
            }}
          >
            {t('detailed_analysis')}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <ChartDataToggler
            selectedDataKey={fuelMeasure}
            handleChange={handleFuelMeasure}
            hiddenDropDownItems={
              assetType === 'fleet'
                ? ['loaded_fuel_consumption', 'unloaded_fuel_consumption']
                : assetType === 'loader'
                ? [
                    'fuel_consumed_per_ton',
                    'loaded_fuel_consumption',
                    'unloaded_fuel_consumption',
                  ]
                : []
            }
          />
          <SortBy
            setSelected={setSortBy}
            options={['high to low', 'low to high']}
            selected={sortBy}
            getOptionLabel={(option) => sortOptionLabels[option] ?? option}
          />
        </Box>
      </Box>
      {isLoadingFuelTreemapData ? (
        <LinearProgress
          color="secondary"
          sx={{
            marginTop: '65px',
          }}
        />
      ) : (
        <>
          <Box
            data-attribute="assetItemBox"
            sx={{
              backgroundColor: enableDarkTheme
                ? palette.boxBackground
                : palette.boxBackground,
              padding: '5px',
            }}
          >
            <TreeMapDiv
              baseCost={{
                diesel: 0,
                gas: 0,
              }}
              sortBy={sortBy as 'high to low' | 'low to high'}
              data={selectedAssetTypTreemapDetails}
              fuelMeasure={fuelMeasure}
              fuelUnit={unitSystem === 'imperial' ? 'gal' : 'ltr'}
              handleClicked={() => {}}
              isLoading={isLoadingFuelTreemapData}
              selectedAssetType={
                assetTypeKey === 'fleet' ? 'haul_truck' : assetType ?? ''
              }
              unitSystem={unitSystem}
              externalTotal={externalTotal}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              gap: '16px',
              marginTop: '16px',
            }}
          >
            {(assetType === 'fleet' || assetType === 'haul_truck') && (
              <FuelAssetAnalyticsSection
                assetType={'haul_truck'}
                // Updated to use standard translation key for haulers
                title={t('std_asset_haulers')}
                aggregation={aggregation}
              />
            )}
            {(assetType === 'fleet' || assetType === 'loader') && (
              <FuelAssetAnalyticsSection
                assetType={'loader'}
                // Updated to use standard translation key for loaders
                title={t('std_asset_loaders')}
                hiddenDropDownItems={[
                  'loaded_fuel_consumption',
                  'unloaded_fuel_consumption',
                ]}
                aggregation={aggregation}
              />
            )}
          </Box>
        </>
      )}
    </>
  );
};

export default DetailedAnalysis;
